import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from "../../ui/dialog";
import { Drawer, DrawerContent } from "../../ui/drawer";
import ManageReservationsTable from '../tables/ManageReservationsTable';
import SpotEarningsSummary from '../rest/SpotEarningsSummary';
import { Button } from "../../ui/button";
import { ChevronLeft, ChevronRight, Calendar, Settings, Car, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ManageSpotModal = ({ 
  spot, 
  isOpen, 
  onClose, 
  reservations, 
  transfers,
  onAddAvailability 
}) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredReservations, setFilteredReservations] = useState([]);
  const [reservationsPerPage, setReservationsPerPage] = useState(3);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    // Check for mobile on mount and when window resizes
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  useEffect(() => {
    // Adjust items per page based on screen size
    const handleResize = () => {
      setReservationsPerPage(window.innerWidth < 768 ? 2 : 3);
    };
    
    handleResize(); // Set initial value
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setFilteredReservations(reservations);
  }, [reservations]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredReservations.length / reservationsPerPage);
  const startIndex = (currentPage - 1) * reservationsPerPage;
  const paginatedReservations = filteredReservations.slice(
    startIndex, 
    startIndex + reservationsPerPage
  );

  // Handle filter changes from the reservations table
  const handleFilterChange = (filteredResults) => {
    setFilteredReservations(filteredResults);
    setCurrentPage(1); // Reset to first page when filters change
  };

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  // Common content for both mobile and desktop
  const ModalContent = () => (
    <>
      {/* Subtle header styling */}
      <div className="bg-primary/5 dark:bg-primary/10 border-b border-primary/10 dark:border-primary/20 p-4 md:p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-primary" />
            <div>
              <h2 className="text-lg font-semibold">
                {spot?.spot_number ? t('parking.manage.title', { spotNumber: spot.spot_number }) : t('parking.manage.loading')}
              </h2>
              <p className="text-xs md:text-sm text-muted-foreground mt-1">
                {t('parking.manage.subtitle')}
              </p>
            </div>
          </div>
          {isMobile && (
            <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="p-4 md:p-6 space-y-6 flex-1 overflow-y-auto">
        {/* Action button at the top for mobile accessibility */}
        <Button
          onClick={onAddAvailability}
          variant="outline"
          className="flex items-center gap-2 w-full sm:w-auto"
        >
          <Calendar className="h-4 w-4" />
          <span>{t('parking.manage.setAvailability')}</span>
        </Button>

        <SpotEarningsSummary 
          reservations={reservations}
          transfers={transfers}
        />

        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 justify-between bg-neutral-50 dark:bg-neutral-900 p-3 rounded-lg border border-neutral-200 dark:border-neutral-800">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <Car className="h-4 w-4 text-neutral-500" />
              <span>{t('parking.manage.reservations')}</span>
            </h4>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="w-full sm:w-auto"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-neutral-500 whitespace-nowrap">
                {t('parking.manage.pagination', { current: currentPage, total: totalPages || 1 })}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="w-full sm:w-auto"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          <ManageReservationsTable 
            reservations={paginatedReservations}
            allReservations={reservations}
            onFilterChange={handleFilterChange}
          />
        </div>
      </div>
    </>
  );

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={onClose}>
        <DrawerContent className="max-h-[90vh]">
          <div className="flex flex-col h-[85vh]">
            <ModalContent />
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto w-[95vw] p-0">
        <ModalContent />
      </DialogContent>
    </Dialog>
  );
};

export default ManageSpotModal;